import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { Form, SubmitButton } from "capitalrx-components-2.0/lib/forms";
import { useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { queryString } from "capitalrx-utilities";

import { Textbox, ZipCodeTextbox } from "@/basic/components";
import { getGeoLocationAPI } from "@/api/axios";

export interface ZipCodeSearchFormValues {
  readonly pharmacy_name: string;
  readonly zip_code: string;
}

interface ZipCodeSearchFormProps {
  readonly pushTo: string;
}

export const ZipCodeSearchForm = ({ pushTo }: ZipCodeSearchFormProps) => {
  const { t: translate } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const form = useRef(null);

  const showAlert = useAlert();

  const { zip_code, pharmacy_name } = queryString.getQueryParams(location.search);

  const failureHandler = useCallback(
    (message?: string) => {
      showAlert({ type: "error", style: "toast", message: message ? message : translate("FORMS.zipCodeSearchForm.anErrorOcurred") });
    },
    [showAlert],
  );

  const handleSubmit = async (formData: ZipCodeSearchFormValues): Promise<void> => {
    const userGeoLocation = await getGeoLocationAPI({ zipCode: formData.zip_code });

    if (!userGeoLocation?.latitude || !userGeoLocation?.longitude) {
      failureHandler(translate("FORMS.zipCodeSearchForm.invalidZipCode"));
      return;
    }

    const lat = userGeoLocation?.latitude;
    const lng = userGeoLocation?.longitude;
    const isValidLocation = !!lat && !!lng;

    if (isValidLocation) {
      const currentQueryStrings = queryString.convertQueryStringToJson(location.search);
      const params = {
        zip_code: formData.zip_code,
        lat,
        lng,
        pharmacy_name: formData.pharmacy_name,
      };

      const urlParams = queryString.convertJsonToQueryString({ ...currentQueryStrings, ...params });

      navigate({
        pathname: pushTo,
        search: urlParams,
      });
    } else {
      form?.current?.setError("zip_code", { type: "custom", message: translate("FORMS.zipCodeSearchForm.invalidZipCode") });
    }
  };

  return (
    <Form onSubmit={handleSubmit} methodsRef={form}>
      <Shelf alignment="top">
        <ZipCodeTextbox
          style={{ flex: 1 }}
          className="FindPharmacyInput"
          name="zip_code"
          label={translate("FORMS.zipCodeSearchForm.zipCode")}
          defaultValue={zip_code?.toString()}
          placeholder={translate("FORMS.zipCodeSearchForm.zipCode")}
          required
        />
        <Textbox
          style={{ flex: 2 }}
          className="FindPharmacyInput"
          name="pharmacy_name"
          label={
            <span>
              {translate("FORMS.zipCodeSearchForm.pharmacyName")} <i>(optional)</i>
            </span>
          }
          defaultValue={pharmacy_name?.toString()}
          placeholder={translate("FORMS.zipCodeSearchForm.pharmacyName")}
        />
        <SubmitButton className="PharmacySearchButton" icon="search" aria-label="search zip code" />
      </Shelf>
    </Form>
  );
};
